/**
 * 目標頁面模組
 */

//-----------------------------------------------------------------------
// 載入模組
import * as React from 'react';
import get from 'lodash/get';
import { useRecordContext, usePermissions, Labeled, TopToolbar, ExportButton } from 'react-admin';
import { List, Show, Edit, SimpleShowLayout, SimpleForm, Datagrid } from 'react-admin';
import { SingleFieldList } from 'ra-ui-materialui';
import { TextField, NumberField, DateField, ShowButton, ReferenceField, UrlField, ReferenceArrayField, ChipField } from 'react-admin';
import { SelectInput, ReferenceInput, ReferenceArrayInput, AutocompleteArrayInput } from 'react-admin';
import { downloadCSV, DataProvider } from 'react-admin';
import { ListEditButton, SaveOnlyToolbar, TextUrlField } from '../lib/components';
import jsonExport from 'jsonexport/dist';

//-----------------------------------------------------------------------
// defines
import { TARGET_MEDIA_TYPE, TARGET_STATUS } from '../common/defines';
import { NamePairInfo } from '../common/interfaces';
import { MemberAuthInfo } from '../providers/authProvider';
import { TimeRangeInput } from '../lib/components';

//-----------------------------------------------------------------------
/** 媒體類型顯示欄位元件。 */

const mediaTypeMap = new Map<string, string>();
const mediaTypeList : NamePairInfo<string>[] =
[
	{ id: TARGET_MEDIA_TYPE.Video,	name: "影片"},
	{ id: TARGET_MEDIA_TYPE.Short,	name: "短影片"},
	{ id: TARGET_MEDIA_TYPE.Live,	name: "直播"},
	{ id: TARGET_MEDIA_TYPE.Post,	name: "貼文"},
];

for(let type of mediaTypeList)
	mediaTypeMap.set(type.id, type.name);

export const MediaTypeField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{mediaTypeMap.get(get(record, source))}</span>);
}

MediaTypeField.defaultProps =
{
	label: '媒體類型',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 狀態顯示欄位元件。 */

const targetStatusMap = new Map<string, string>();
const targetStatusList : NamePairInfo<string>[] =
[
	{ id: TARGET_STATUS.Tracking,	name: "追蹤中"},
	{ id: TARGET_STATUS.Untracked,	name: "停止追蹤"}
];

for(let status of targetStatusList)
	targetStatusMap.set(status.id, status.name);

export const TargetStatusField = (props: any) =>
{
	const { source } = props;
	const record = useRecordContext(props);

	return (<span className="MuiTypography-body2 MuiTypography-root">{targetStatusMap.get(get(record, source))}</span>);
}

TargetStatusField.defaultProps =
{
	label: '狀態',
	addLabel: true,
};

//-----------------------------------------------------------------------
/** 目標列表過濾器。 */
const targetFilters =
[
	<ReferenceInput key='input-source' label='資料來源' source='sourceId' reference='source' resettable alwaysOn >
		<SelectInput optionText='name' />
	</ReferenceInput>,
	<ReferenceArrayInput key='input-region' label='區域' source='region' reference='regionAll'>
		<AutocompleteArrayInput optionText='name' />
	</ReferenceArrayInput>,
	<ReferenceInput key='input-keyword' label='關鍵字' source='keywordId' reference='keywordAll' resettable alwaysOn >
		<SelectInput optionText='text' />
	</ReferenceInput>,
	<SelectInput key='input-mediaType' label='媒體類型' source='type' choices={mediaTypeList} resettable alwaysOn />,
	<SelectInput key='input-status' label='狀態' source='status' choices={targetStatusList} resettable alwaysOn />,
	<TimeRangeInput key='input-timeRange' alwaysOn />
];

const ListActions = () => (
    <TopToolbar>
        <ExportButton maxResults={1000000} />
    </TopToolbar>
);

/** 資料輸出功能。 */
const exporter = (records: any, fetchRelatedRecords: (data: any, field: string, resource: string) => Promise<any>, dataProvider: DataProvider) =>
{
	fetchRelatedRecords(records, 'sourceId', 'source').then((sources) =>
	{
		fetchRelatedRecords(records, 'regionId', 'region').then((regions) =>
		{
			fetchRelatedRecords(records, 'channelId', 'channel').then((channels) =>
			{
				fetchRelatedRecords(records, 'keywordIds', 'keyword').then((keywords) =>
				{
					const data = records.map((record: any) =>
					{
						let	keywordIds = record.keywordIds as number[];
						let keywordList : string[] = [];

						for(let keywordId of keywordIds)
						{
							if(keywords[keywordId])
								keywordList.push(keywords[keywordId].text);
						}

						return {
							...record,
							sourceId: sources[record.sourceId].name,
							regionId: regions[record.regionId].name,
							channelId: channels[record.channelId].name,
							keywordIds: keywordList
						};
					});

					return jsonExport(data,
					{
						headers	: [ 'id', 'sourceId', 'regionId', 'channelId', 'keywordIds', 'title', 'url', 'viewCount', 'viewIncrease', 'commentCount', 'commentIncrease', 'status', 'publishTime' ],
						rename	: [ 'id', '資料來源', '區域', '所屬頻道', '關鍵字', '標題', '目標網址', '累計瀏覽量', '瀏覽增加量', '留言數量', '留言增加量', '狀態', '發布日期' ]
					}, (err, csv) =>
					{
						downloadCSV("\ufeff" + csv, 'target');
					});
				});
			});
		});
	});
};

/** 目標列表頁面。 */
export const TargetList = (props: any) =>
{
	let { permissions } = usePermissions();
	let	authInfo = permissions as MemberAuthInfo;

    return <List {...props} title='目標' sort={{ field: 'id', order: 'ASC' }} filters={targetFilters} actions={<ListActions />} exporter={exporter}>
        <Datagrid bulkActionButtons={false}>
            <TextField source='id' />
			<ReferenceField label="資料來源" source="sourceId" reference="source">
                <TextField source="name" />
            </ReferenceField>
			<ReferenceField label="區域" source="regionId" reference="region">
				<TextField source="name" />
			</ReferenceField>
			<ReferenceField label="所屬頻道" source="channelId" reference="channel">
                <TextField source="name" />
            </ReferenceField>
			<ReferenceArrayField label="關鍵字" source="keywordIds" reference="keyword" sortable={false} >
				<SingleFieldList>
					<ChipField source="text" />
				</SingleFieldList>
			</ReferenceArrayField>
			<TextUrlField source='url' textSource='title' label='標題' target='_blank' sortable={false} />
            <MediaTypeField source='mediaType' />
            <NumberField source='viewCount' label='瀏覽量' />
            <NumberField source='viewIncrease' label='瀏覽增加量' />
			<NumberField source='reactCount' label='累計反應量' />
			<NumberField source='reactIncrease' label='反應增加量' />
            <NumberField source='commentCount' label='留言數量' />
            <NumberField source='commentIncrease' label='留言增加量' />
            <TargetStatusField source='status' />
			<DateField source='publishTime' label='發布日期' />
			<ListEditButton edit={authInfo.targetEdit} />
			<ShowButton />
        </Datagrid>
    </List>;
};

/** 目標資訊頁面。 */
export const TargetShow = (props: any) => (
    <Show {...props}>
		<SimpleShowLayout>
			<TextField source='id' />
			<ReferenceField label="資料來源" source="sourceId" reference="source" >
                <TextField source="name" />
            </ReferenceField>
			<ReferenceField label="區域" source="regionId" reference="region">
				<TextField source="name" />
			</ReferenceField>
			<ReferenceField label="所屬頻道" source="channelId" reference="channel" >
                <TextField source="name" />
            </ReferenceField>
			<ReferenceArrayField label="關鍵字" source="keywordIds" reference="keyword" >
				<SingleFieldList>
					<ChipField source="text" />
				</SingleFieldList>
			</ReferenceArrayField>
            <TextField source='title' label='標題' />
            <TextField source='description' label='說明' />
            <UrlField source='url' label='網址' target='_blank' />
            <TargetStatusField source='status' />
			<DateField source='publishTime' label='發布日期' />
		</SimpleShowLayout>
	</Show>
);

/** 目標編輯頁面。 */
export const TargetEdit = (props: any) => (
    <Edit {...props}>
		<SimpleForm toolbar={<SaveOnlyToolbar/>}>
			<Labeled label="ID"><TextField source='id' /></Labeled>
			<Labeled label="資料來源">
				<ReferenceField source="sourceId" reference="source" >
					<TextField source="name" />
				</ReferenceField>
			</Labeled>
			<Labeled label="區域">
				<ReferenceField source="regionId" reference="region">
					<TextField source="name" />
				</ReferenceField>
			</Labeled>
			<Labeled label="所屬頻道">
				<ReferenceField source="channelId" reference="channel" >
					<TextField source="name" />
				</ReferenceField>
			</Labeled>
			<Labeled label="關鍵字">
				<ReferenceArrayField source="keywordIds" reference="keyword" >
					<SingleFieldList>
						<ChipField source="text" />
					</SingleFieldList>
				</ReferenceArrayField>
			</Labeled>
			<Labeled label="標題"><TextField source='title' /></Labeled>
			<Labeled label="說明"><TextField source='description' /></Labeled>
			<Labeled label="網址"><UrlField source='url' target='_blank' /></Labeled>
			<SelectInput source='status' choices={mediaTypeList} label="狀態" />
			<Labeled label="發布日期"><DateField source='publishTime' /></Labeled>
		</SimpleForm>
	</Edit>
);
